import { useState, type ComponentProps, type ReactNode } from 'react';
import { Dialog } from 'radix-ui';
import { Button } from './components';

/*
 * Stacking order for the whole app. CSS paints positioned elements with a
 * POSITIVE z-index after those with z-index auto, so an overlay without a
 * z-index is punched through by any sticky header that has one. Every layer
 * that floats above the page therefore declares its level here:
 *
 *   10-30   in-page layers: sticky table headers, sticky row labels,
 *           the Gantt's today line, combobox dropdowns
 *   40/50   the phone menu backdrop and slide-over (Layout)
 *   50      popovers anchored to the page (Filter, Legend)
 *   60/70   modal overlay / modal content
 *   100     toasts, so an Undo toast raised from inside a modal is readable
 *
 * Modals are only ever built with this component; a test asserts that no
 * other file reaches for Dialog.Content directly.
 */

/** Branded modal: navy header, gold rule, scrollable body, footer slot. */
export function Modal({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  width = 'md',
}: {
  open: boolean;
  onOpenChange(open: boolean): void;
  title: string;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
  width?: 'sm' | 'md' | 'lg' | 'xl';
}) {
  const w = { sm: 'w-[420px]', md: 'w-[560px]', lg: 'w-[760px]', xl: 'w-[960px]' }[width];
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[60] bg-ink/30" />
        <Dialog.Content
          className={`fixed left-1/2 top-1/2 z-[70] ${w} max-w-[calc(100vw-2rem)] -translate-x-1/2 -translate-y-1/2 rounded-lg bg-white shadow-xl focus:outline-none`}
        >
          <div className="rounded-t-lg bg-navy px-5 py-3 text-white">
            <Dialog.Title className="text-base font-semibold">{title}</Dialog.Title>
            {description ? (
              <Dialog.Description className="text-xs text-white/75">{description}</Dialog.Description>
            ) : (
              <Dialog.Description className="sr-only">{title}</Dialog.Description>
            )}
          </div>
          <div className="h-1 bg-gold" />
          <div className="max-h-[70vh] overflow-y-auto px-5 py-4">{children}</div>
          {footer && <div className="flex justify-end gap-2 border-t border-line px-5 py-3">{footer}</div>}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  children,
  confirmLabel = 'Confirm',
  danger = false,
  onConfirm,
  busy = false,
}: {
  open: boolean;
  onOpenChange(open: boolean): void;
  title: string;
  children: ReactNode;
  confirmLabel?: string;
  danger?: boolean;
  onConfirm(): void | Promise<void>;
  busy?: boolean;
}) {
  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      width="sm"
      footer={
        <>
          <Button onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button variant={danger ? 'danger' : 'primary'} busy={busy} onClick={() => void onConfirm()}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="text-sm">{children}</div>
    </Modal>
  );
}

/**
 * A button whose action is asked about first. Everything that removes,
 * clears or archives goes through one of these, so "are you sure" is never
 * something a screen forgets. The wrapper stops the click (and the dialog's
 * clicks, which React bubbles through the portal) from reaching a clickable
 * card the button sits in.
 */
export function ConfirmButton({ title, message = 'This cannot be undone.', confirmLabel = 'Remove', danger = true, onConfirm, children, ...button }: Omit<ComponentProps<typeof Button>, 'onClick'> & { title: string; message?: string; confirmLabel?: string; danger?: boolean; onConfirm(): unknown }) {
  const [open, setOpen] = useState(false);
  return (
    <span onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()} className="contents">
      <Button {...button} onClick={() => setOpen(true)}>{children}</Button>
      <ConfirmDialog open={open} onOpenChange={setOpen} title={title} confirmLabel={confirmLabel} danger={danger} onConfirm={() => { setOpen(false); void onConfirm(); }}>
        {message}
      </ConfirmDialog>
    </span>
  );
}
